import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FUNNEL_STAGE_LABELS,
  HelpTip,
  formatDate,
  PersonDisplay,
  TeamRoleChips,
  accessRoleLabel,
} from '@dataroom/shared';
import type { AccessGrant, AccessRole, GrantWarning, RoomAdminRole } from '@dataroom/shared';

/** Plain-English party labels (the raw roles read as internal jargon). */
const ROLE_LABEL: Record<AccessRole, string> = {
  seller_team: 'Seller side',
  intermediary_team: 'Intermediary',
  diligence_team: 'Buyer side',
  custom: 'Custom access',
};

/** In a buyer-run room the roles read from the buyer's side (ADR-0039). */
function roleLabel(role: AccessRole, adminRole: RoomAdminRole | null | undefined): string {
  return adminRole === 'buyer' && (role === 'diligence_team' || role === 'seller_team')
    ? accessRoleLabel(role, adminRole)
    : ROLE_LABEL[role];
}

/** What a grant lets the person do, most-restrictive first. Everyone with a
 * grant can at least view; download/edit are added on top. */
function canDo(grant: AccessGrant): string {
  const parts = ['View'];
  if (grant.downloadAllowed) parts.push('Download');
  if (grant.writeAllowed) parts.push('Edit');
  return parts.length === 1 ? 'View only' : parts.join(' · ');
}

/** What the person can see, in plain words. */
function canSee(grant: AccessGrant): string {
  const n = grant.tagAreaScope.length;
  if (n === 0) return 'Nothing yet';
  return `${n} document area${n === 1 ? '' : 's'}`;
}

/** Whose team roles the admin may change from here: a seller's-team person. A
 * buyer's team member is edited from that buyer's team (the server only lets the
 * admin set roles for the seller team and named buyer-team members). */
function rolesEditable(grant: AccessGrant, sellerIds: ReadonlySet<string>): boolean {
  return (
    grant.role === 'seller_team' &&
    !grant.revokedAt &&
    Boolean(grant.actorId) &&
    !sellerIds.has(grant.actorId as string)
  );
}

/** Roles show beside a seller's-team or buyer-side person. */
function rolesShown(grant: AccessGrant): boolean {
  return grant.role === 'seller_team' || grant.role === 'diligence_team';
}

interface AccessRightsCardProps {
  grants: AccessGrant[];
  loading: boolean;
  error: string | null;
  onInvite: () => void;
  onAddGrant: () => void;
  onRevoke: (grantId: string) => void;
  /** Open the team-roles editor for this grantee (a seller's-team person). */
  onEditRoles?: (grant: AccessGrant) => void;
  /** The room's sellers (a seller is not a team member, so has no roles to edit). */
  sellerActorIds?: readonly string[];
  /** Grant id currently being revoked (disables its button). */
  revokingId: string | null;
  /** Warnings echoed on a successful (acknowledged) create — informational. */
  ackWarnings: GrantWarning[];
  onDismissAckWarnings: () => void;
  /** The room's admin role — labels read from the buyer's side in a buyer-run room. */
  adminRole?: RoomAdminRole | null;
}

function Badge({ children, tone }: { children: string; tone: 'on' | 'off' | 'revoked' }) {
  const styles =
    tone === 'on'
      ? 'bg-st-certified text-st-certified-fg'
      : tone === 'revoked'
        ? 'bg-destructive/10 text-destructive'
        : 'bg-muted text-muted-foreground';
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${styles}`}>{children}</span>
  );
}

function duration(grant: AccessGrant): string {
  if (grant.ongoing) return 'Ongoing';
  if (grant.expiresAt) return `Until ${formatDate(grant.expiresAt)}`;
  return 'One-off';
}

/**
 * PRD-0004 §3b Access Rights — the room's grants with invite / add-grant /
 * revoke actions. The serialized grant carries no actor/role (see the PR's
 * owner-review note), so each row is described by its scope, funnel gate,
 * permissions and duration. Presentational: the page owns the dialogs and
 * mutations.
 */
export function AccessRightsCard({
  grants,
  loading,
  error,
  onInvite,
  onAddGrant,
  onRevoke,
  onEditRoles,
  sellerActorIds = [],
  revokingId,
  ackWarnings,
  onDismissAckWarnings,
  adminRole,
}: AccessRightsCardProps) {
  const sellerIds = new Set(sellerActorIds);
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle>Access rights</CardTitle>
            <p className="text-sm text-muted-foreground">
              Who can see what in this room — invite new people, and manage each person&apos;s
              access.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onInvite}>
              Invite to room
            </Button>
            <Button size="sm" onClick={onAddGrant}>
              Manage access rights
            </Button>
            <HelpTip id="admin.access.manage-rights" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {ackWarnings.length > 0 ? (
          <div
            role="status"
            className="rounded-md border border-st-check-line bg-st-check p-3 text-st-check-fg"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-medium">Grant created — advisories acknowledged</p>
                <ul className="mt-1.5 list-disc space-y-1 pl-5 text-sm">
                  {ackWarnings.map((w) => (
                    <li key={w.code}>{w.message}</li>
                  ))}
                </ul>
              </div>
              <button
                type="button"
                onClick={onDismissAckWarnings}
                className="text-xs font-medium underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        ) : null}

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : grants.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="py-2 font-medium">Person</th>
                <th className="py-2 font-medium">Can see</th>
                <th className="py-2 font-medium">Can do</th>
                <th className="py-2 font-medium">Available</th>
                <th className="py-2 font-medium">Status</th>
                <th className="py-2 font-medium">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {grants.map((grant) => {
                const revoked = Boolean(grant.revokedAt);
                return (
                  <tr key={grant.id} className="border-b align-top last:border-0">
                    <td className="py-2 pr-4">
                      <PersonDisplay email={grant.actorEmail} size="sm" fallback="Unknown" />
                      {grant.role ? (
                        <div className="text-xs text-muted-foreground">
                          {roleLabel(grant.role, adminRole)}
                        </div>
                      ) : null}
                      {rolesShown(grant) ? (
                        <TeamRoleChips roles={grant.teamRoles} className="mt-1" />
                      ) : null}
                    </td>
                    <td className="py-2 pr-4">
                      {canSee(grant)}
                      {grant.excludedTagIds.length > 0 ? (
                        <span className="text-xs text-muted-foreground">
                          {' '}
                          (−{grant.excludedTagIds.length} hidden)
                        </span>
                      ) : null}
                    </td>
                    <td className="py-2 pr-4">{canDo(grant)}</td>
                    <td className="py-2 pr-4">
                      <div>
                        {grant.funnelStage
                          ? `Unlocks at ${FUNNEL_STAGE_LABELS[grant.funnelStage]}`
                          : 'Now'}
                      </div>
                      <div className="text-xs text-muted-foreground">{duration(grant)}</div>
                    </td>
                    <td className="py-2 pr-4">
                      {revoked ? (
                        <Badge tone="revoked">Revoked</Badge>
                      ) : (
                        <Badge tone="on">Live</Badge>
                      )}
                    </td>
                    <td className="py-2 text-right">
                      {revoked ? (
                        <span className="text-xs text-muted-foreground">—</span>
                      ) : (
                        <div className="flex flex-col items-end gap-1">
                          {onEditRoles && rolesEditable(grant, sellerIds) ? (
                            <button
                              type="button"
                              onClick={() => onEditRoles(grant)}
                              className="text-xs font-medium text-primary hover:underline"
                            >
                              Roles
                            </button>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => onRevoke(grant.id)}
                            disabled={revokingId === grant.id}
                            className="text-xs font-medium text-destructive hover:underline disabled:opacity-50"
                          >
                            {revokingId === grant.id ? 'Revoking…' : 'Revoke'}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-muted-foreground">
            No access grants yet. Invite a party to apply a default grant, or add one manually.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
